import { useEffect, useRef, useMemo, useState } from "react";
import type { ComponentType, SVGProps } from "react";

import { Input, Textarea, Label } from "@/components/atoms";
import {
    ProductSelector,
    LevelSelector,
    SegmentSelector,
    MetricSelector,
    WidgetTypeSelector,
    SegmentOptionsToggle,
    WidgetTypeOption,
    ColorInput,
    DateSelector,
    DataFiltersSection,
    DatePreset,
} from "@/components/molecules/ManualWidgetForm";
import { useProductHierarchy } from "@/modules/product/hooks/useProductHierarchy";
import { useFilteredProductHierarchy } from "@/modules/product/hooks/useFilteredProductHierarchy";

import { Activity, BarChart3, BarChart2, Table2, PieChart, TrendingUp, Filter, Bubbles, Info } from "lucide-react";
import { ChartDataIcon, PaletteIcon } from "@/components/icons";
import { ChevronDownIcon } from "@/components/icons";
import { AdvancedSettings } from "./AdvancedSettings";
import { Metric, Service } from "@/modules/product/types";
import { extractWidgetToDraft } from "@/modules/widgets/utils/widgetTransform";
import { Switch } from "@/components/molecules/Switch";
import { WidgetTemplateConfig } from "@/modules/widget-templates";
import { DraftDataFilter, DataFilterFieldOption, isDraftFilterComplete } from "@/modules/widgets/types/dataFilters";
import { levelToDimensionField } from "@/modules/widgets/utils/levelDimensionField";

interface CustomWidgetDraft {
    title: string;
    description: string;
    widgetType: WidgetTypeOption | null;
    productIds: number[];
    levelId: number | null;
    segmentId: number | null;
    metricIds: number[];
    /** Subset of metricIds marked as hidden-by-default ("dynamic"); persisted to metadata.dynamicMetrics (by name). */
    dynamicMetricIds: number[];
    groupBySegment: boolean;
    drilldownBySegment: boolean;
    publishToAll: boolean;
    widgetColors: string[];
    filterByProduct: boolean;
    filterByDimension: boolean;
    usePageDate: boolean;
    datePreset: DatePreset;
    customDateRange: { from?: Date | null; to?: Date | null };

    // * Metadata
    tableSummaryRow: "none" | "sum" | "average";
    productLevel?: boolean;
    showMetricSelector?: boolean;

    // * AI Insights
    enableAIInsights?: boolean;
    publishInsights?: boolean;

    // * Custom widget type — stores the real backend type (e.g. "top-offers") when widgetType is "custom"
    realWidgetType?: string;

    // * Row filters applied at data-fetch time (AND-joined)
    dataFilters: DraftDataFilter[];
}

type ExtractedWidgetDraft = Partial<CustomWidgetDraft> & {
    metrics?: Metric[];
    services?: Service[];
    levelName?: string;
    segmentName?: string;
    metricNames?: string[];
    dynamicMetricNames?: string[];
};

interface CustomWidgetPanelProps {
    onDraftChange?: (state: {
        draft: CustomWidgetDraft;
        isValid: boolean;
        isLoading?: boolean;
        hierarchyData?: {
            products: Array<{
                id: number;
                name: string;
                services: Array<{ id: number; name: string }>;
            }>;
            levels: Array<{ id: number; name: string; serviceId: number }>;
            segments: Array<{ id: number; name: string; levelId: number }>;
            metrics: Array<{ id: number; name: string; showName: string; category?: string; serviceId: number }>;
        };
    }) => void;
    formRef?: React.RefObject<HTMLFormElement | null>;
    editWidget?: any;
    isDuplicating?: boolean;
    isSharedClone?: boolean;
    templateConfig?: WidgetTemplateConfig | null;
}

const DEFAULT_DATE_PRESET: DatePreset = "last_30_days";

const mapLegacyPresetToNew = (preset?: string | null): DatePreset | undefined => {
    switch (preset) {
        case "previous_month":
            return "last_month";
        case "last_3_months":
            return "last_90_days";
        case "last_12_months":
        case "yesterday":
        case "last_7_days":
        case "last_14_days":
        case "last_30_days":
        case "last_90_days":
        case "this_month":
        case "last_month":
        case "this_year":
        case "last_year":
        case "custom":
            return preset as DatePreset;
        default:
            return undefined;
    }
};

const WIDGET_TYPE_OPTIONS: {
    label: string;
    value: WidgetTypeOption;
    description: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
}[] = [
        {
            value: "ticker",
            label: "Ticker",
            icon: Activity,
            description: "Single metric with trend",
        },
        {
            value: "bar",
            label: "Bar Chart",
            icon: BarChart3,
            description: "Compare values across categories",
        },
        {
            value: "line",
            label: "Line Chart",
            icon: TrendingUp,
            description: "Track trends over time",
        },
        {
            value: "funnel",
            label: "Funnel",
            icon: Filter,
            description: "Visualize conversion stages",
        },
        {
            value: "table",
            label: "Table",
            icon: Table2,
            description: "Detailed data in rows",
        },
        {
            value: "pie",
            label: "Pie Chart",
            icon: PieChart,
            description: "Show parts of a whole",
        },
        {
            value: "bubble",
            label: "Bubble Chart",
            icon: Bubbles,
            description: "Compare values by size",
        },
        {
            value: "stacked-bar-line",
            label: "Combo Chart",
            icon: BarChart2,
            description: "Bars + line on dual axes (2 metrics)",
        },
    ];

export function CustomWidgetPanel({ onDraftChange, formRef: externalFormRef, editWidget, isDuplicating = false, isSharedClone = false, templateConfig }: CustomWidgetPanelProps) {
    const internalFormRef = useRef<HTMLFormElement>(null);
    const formRef = externalFormRef || internalFormRef;

    const {
        products,
        services,
        levels,
        segments,
        metrics,
        isLoading,
        isLoadingProducts,
        isLoadingProductDetails,
        isLoadingMetrics,
        isError,
        error,
        loadProductDetails,
        loadServiceMetrics,
        loadedServiceMetricIds,
        servicesByProductId: servicesByProductIdMap,
    } = useProductHierarchy();

    const normalizeTemplateName = (value?: string) => {
        if (!value) return "";
        return value
            .toLowerCase()
            .replace(/[_-]+/g, " ")
            .replace(/[^a-z0-9 ]/g, "")
            .replace(/\s+/g, " ")
            .trim();
    };

    // Initialize from edit widget or template config
    const initialDraft = useMemo<ExtractedWidgetDraft>(() => {
        if (editWidget) return extractWidgetToDraft(editWidget, metrics, services) as ExtractedWidgetDraft;
        if (templateConfig) {
            return {
                title: templateConfig.title,
                description: templateConfig.description,
                widgetType: (templateConfig.chartType || templateConfig.widgetType) as WidgetTypeOption,
                productIds: templateConfig.products || [],
                levelName: templateConfig.level,
                segmentName: templateConfig.segment,
                metricNames: templateConfig.metrics,
                groupBySegment: templateConfig.groupBySegment,
            };
        }
        return {};
    }, [editWidget, templateConfig]);

    // The widget type the source widget/template was originally created with. Existing
    // widgets and templates may have been built under different (or looser) rules than the
    // current creation validation enforces — e.g. a line chart whose metric count the rules
    // now reject, or a custom / DB-modified widget. We always keep their original type
    // enabled instead of disabling or clearing it, so these widgets can still be edited or
    // duplicated even when they no longer match the rules for a brand-new widget.
    const sourceWidgetType = useMemo<WidgetTypeOption | null>(
        () => (editWidget || templateConfig ? ((initialDraft.widgetType as WidgetTypeOption) ?? null) : null),
        [editWidget, templateConfig, initialDraft.widgetType],
    );

    // Track resolution phases separately to avoid deadlock:
    // metrics only load after level is selected, so we can't wait for all three at once
    const hasResolvedLevelSegment = useRef(false);
    const hasResolvedMetrics = useRef(false);
    const hasResolvedIds = useRef(false);

    const [title, setTitle] = useState(
        isDuplicating && !isSharedClone && initialDraft.title ? `${initialDraft.title} - Copy` : initialDraft.title || ""
    );
    const [description, setDescription] = useState(initialDraft.description || "");
    const [widgetType, setWidgetType] = useState<WidgetTypeOption | null>((initialDraft.widgetType as WidgetTypeOption) || null);
    const [selectedProducts, setSelectedProducts] = useState<number[]>(initialDraft.productIds || []);
    const [selectedLevelId, setSelectedLevelId] = useState<number | null>(initialDraft.levelId || null);
    const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(initialDraft.segmentId || null);
    const [selectedMetricIds, setSelectedMetricIds] = useState<number[]>(initialDraft.metricIds || []);
    const [dynamicMetricIds, setDynamicMetricIds] = useState<number[]>(initialDraft.dynamicMetricIds || []);
    const [groupBySegment, setGroupBySegment] = useState(initialDraft.groupBySegment || false);
    const [productLevel, setProductLevel] = useState<boolean>(initialDraft.productLevel || false) // * Synthetic level - Metadata field.  
    const [showMetricSelector, setShowMetricSelector] = useState<boolean>(initialDraft.showMetricSelector ?? false);  
    // const [drilldownBySegment, setDrilldownBySegment] = useState(true);
    const [publishToAll, setPublishToAll] = useState(isDuplicating ? false : initialDraft.publishToAll === true);
    const [showAxisLegends, setShowAxisLegends] = useState(false);
    const [widgetColors, setWidgetColors] = useState<string[]>(
        initialDraft.widgetColors || ["#228995", "#74c4c6", "#f1883c", "#dc5c1f", "#eab234", "#71c7a7", "#9f4462", "#e15e64"]
    );
    const [usePageDate, setUsePageDate] = useState(initialDraft.usePageDate ?? true);
    const normalizedInitialPreset = mapLegacyPresetToNew(initialDraft.datePreset as string | undefined);
    const [datePreset, setDatePreset] = useState<DatePreset>(normalizedInitialPreset ?? DEFAULT_DATE_PRESET);
    const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>(() => {
        const fromValue = initialDraft.customDateRange?.from ? new Date(initialDraft.customDateRange.from) : undefined;
        const toValue = initialDraft.customDateRange?.to ? new Date(initialDraft.customDateRange.to) : undefined;
        return { from: fromValue, to: toValue };
    });

    const [isDataOpen, setIsDataOpen] = useState(true);
    const [isWidgetSelectorOpen, setIsWidgetSelectorOpen] = useState(false);

    // * Advanced settings section states
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [filterByProduct, setFilterByProduct] = useState(initialDraft.filterByProduct ?? false);
    const [filterByDimension, setFilterByDimension] = useState(initialDraft.filterByDimension ?? false);
    const [tableSummaryRow, setTableSummaryRow] = useState<"none" | "sum" | "average">(initialDraft.tableSummaryRow ?? "none");
    const [enableAIInsights, setEnableAIInsights] = useState(initialDraft.enableAIInsights ?? false);
    const [publishInsights, setPublishInsights] = useState(initialDraft.publishInsights ?? false);
    const [dataFilters, setDataFilters] = useState<DraftDataFilter[]>((initialDraft.dataFilters as DraftDataFilter[]) ?? []);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    //* -----------------------------------------

    useEffect(() => {
        if (selectedProducts.length > 0) {
            loadProductDetails(selectedProducts);
        }
    }, [selectedProducts, loadProductDetails]);

    useEffect(() => {
        if (!selectedLevelId) return;
        const serviceIds = selectedProducts.flatMap((pid) => servicesByProductIdMap[pid]?.map((s) => s.id) || []);
        if (serviceIds.length > 0) {
            loadServiceMetrics(serviceIds);
        }
    }, [selectedProducts, servicesByProductIdMap, loadServiceMetrics, selectedLevelId]);

    const servicesByProductId = useMemo(() => {
        return services.reduce<Record<number, number[]>>((acc, service) => {
            if (!acc[service.productId]) {
                acc[service.productId] = [];
            }
            acc[service.productId].push(service.id);
            return acc;
        }, {});
    }, [services]);

    // True only when every selected product already has its services in the local cache.
    // Used to prevent validation effects from clearing level/metrics while a newly-added
    // product's data is still being fetched (servicesByProductId would be incomplete).
    const allProductsHaveServices = useMemo(
        () => selectedProducts.length === 0 || selectedProducts.every((pid) => servicesByProductId[pid] !== undefined),
        [selectedProducts, servicesByProductId]
    );

    // True only when all service IDs for every selected product have their metrics loaded.
    // Guards the metrics validation effect: loadServiceMetrics is async and its setPendingMetricsLoads
    // call doesn't reflect in isLoadingMetrics until the next render, so we track completion via state.
    const allServiceMetricsLoaded = useMemo(() => {
        if (!selectedLevelId || selectedProducts.length === 0) return true;
        return selectedProducts.every((pid) => {
            const svcIds = servicesByProductId[pid];
            if (!svcIds || svcIds.length === 0) return false;
            return svcIds.every((sid) => loadedServiceMetricIds.has(sid));
        });
    }, [selectedLevelId, selectedProducts, servicesByProductId, loadedServiceMetricIds]);

    const {
        availableLevels,
        availableSegments: availableSegmentsBase,
        availableMetrics,
    } = useFilteredProductHierarchy({
        selectedProductIds: selectedProducts,
        selectedLevelId,
        servicesByProductId,
        levels,
        segments,
        metrics,
    });

    const availableSegments = useMemo(() => {
        if (!selectedLevelId) return availableSegmentsBase;

        const selectedLevel = levels.find((l) => l.id === selectedLevelId);
        const selectedLevelName = selectedLevel?.name.toLowerCase();
        const selectedServiceName = services.find((s) => s.id === selectedLevel?.serviceId)?.name.toLowerCase();
        const isPaidSearch = selectedServiceName === "paidsearch";

        if (isPaidSearch && selectedLevelName === "ad_group") {
            return availableSegmentsBase.filter((s) => s.name.toLowerCase() !== "location");
        }

        return availableSegmentsBase;
    }, [availableSegmentsBase, levels, services, selectedLevelId]);

    const selectedMetrics: Metric[] = useMemo(() => {
        return selectedMetricIds.map((metricId) => metrics.find((metric) => metric.id === metricId)!);
    }, [metrics, selectedMetricIds]);

    const selectedSegmentForFilter = useMemo(
        () => (selectedSegmentId ? segments.find((s) => s.id === selectedSegmentId) : undefined),
        [selectedSegmentId, segments],
    );
    const isCampaignSegmentSelected = (selectedSegmentForFilter?.name ?? "").toLowerCase() === "campaign";
    const campaignSegmentLabel = selectedSegmentForFilter?.displayName ?? selectedSegmentForFilter?.name;

    const selectedLevelForFilter = useMemo(
        () => (selectedLevelId ? levels.find((l) => l.id === selectedLevelId) : undefined),
        [selectedLevelId, levels],
    );
    // The campaign level counts only when it's the actual breakdown — not when the widget
    // is grouped by a sub-segment (in which case that segment is the filter dimension).
    const willBreakdownBySegment = groupBySegment && !!selectedSegmentId;
    const isCampaignLevelSelected = !willBreakdownBySegment && (selectedLevelForFilter?.name ?? "").toLowerCase() === "campaign";

    const filterFieldOptions: DataFilterFieldOption[] = useMemo(() => {
        const options: DataFilterFieldOption[] = [];
        const seenFields = new Set<string>();
        const push = (opt: DataFilterFieldOption) => {
            if (!opt.field || seenFields.has(opt.field)) return;
            seenFields.add(opt.field);
            options.push(opt);
        };

        const selectedLevel = selectedLevelId ? levels.find((l) => l.id === selectedLevelId) : undefined;
        if (selectedLevel) {
            push({
                field: levelToDimensionField(selectedLevel.name),
                label: selectedLevel.displayName ?? selectedLevel.name,
                kind: "string",
            });
        }
        const selectedSegment = selectedSegmentId ? segments.find((s) => s.id === selectedSegmentId) : undefined;
        if (selectedSegment) {
            push({ field: "segmentValue", label: selectedSegment.displayName ?? selectedSegment.name, kind: "string" });
        }
        selectedMetrics.forEach((m) => {
            if (!m) return;
            push({
                field: m.name,
                label: m.showName,
                kind: m.metricType === "text" ? "string" : "number",
            });
        });
        return options;
    }, [selectedLevelId, selectedSegmentId, selectedMetrics, levels, segments]);

    useEffect(() => {
        // Don't prune while edit-mode is still resolving level/segment/metric
        // ids — `filterFieldOptions` would be momentarily empty and we'd wipe
        // saved filters before the user's selections settle.
        if ((editWidget || templateConfig) && !hasResolvedIds.current) return;
        if (!dataFilters.length) return;
        const validFields = new Set(filterFieldOptions.map((o) => o.field));
        // Only prune rows whose field was previously valid but is now gone
        // (e.g. user removed the metric this filter referenced). Empty-field
        // rows are in-progress and must survive.
        const next = dataFilters.filter((f) => f.field === "" || validFields.has(f.field));
        if (next.length !== dataFilters.length) setDataFilters(next);
    }, [filterFieldOptions, dataFilters, editWidget, templateConfig]);

    useEffect(() => {
        if (((editWidget || templateConfig) && !hasResolvedIds.current)) {
            return;
        }
        if (!allProductsHaveServices) return;
        if (selectedLevelId && !availableLevels.some((level) => level.id === selectedLevelId)) {
            setSelectedLevelId(null);
        }
    }, [allProductsHaveServices, availableLevels, editWidget, selectedLevelId]);

    useEffect(() => {
        if (((editWidget || templateConfig) && !hasResolvedIds.current)) {
            return;
        }
        if (!allProductsHaveServices) return;
        if (selectedSegmentId && !availableSegments.some((segment) => segment.id === selectedSegmentId)) {
            setSelectedSegmentId(null);
            setGroupBySegment(false);
        }
    }, [allProductsHaveServices, availableSegments, editWidget, selectedSegmentId]);

    useEffect(() => {
        if (((editWidget || templateConfig) && !hasResolvedIds.current)) {
            return;
        }
        if (!allProductsHaveServices || !allServiceMetricsLoaded) return;
        const availableMetricIds = new Set(availableMetrics.map((metric) => metric.id));
        const filteredSelection = selectedMetricIds.filter((metricId) => availableMetricIds.has(metricId));
        if (filteredSelection.length !== selectedMetricIds.length) {
            setSelectedMetricIds(filteredSelection);
        }
    }, [allProductsHaveServices, allServiceMetricsLoaded, availableMetrics, editWidget, selectedMetricIds]);

    // Phase 1: Resolve level + segment IDs when availableLevels load
    // Uses availableLevels (filtered by selected products) so the resolved ID
    // is guaranteed to pass the level validation effect.
    useEffect(() => {
        if (!(editWidget || templateConfig) || hasResolvedLevelSegment.current) return;
        if (availableLevels.length === 0) return;

        let resolvedLevelId = selectedLevelId;

        if (initialDraft.levelName && !selectedLevelId) {
            const normalizedLevelName = normalizeTemplateName(initialDraft.levelName);
            const level = availableLevels.find((l) => {
                const normalizedName = normalizeTemplateName(l.name);
                const normalizedDisplayName = normalizeTemplateName(l.displayName);
                return normalizedName === normalizedLevelName || normalizedDisplayName === normalizedLevelName;
            });
            if (level) {
                resolvedLevelId = level.id;
                setSelectedLevelId(level.id);
            } else {
                // Level not found yet in availableLevels — don't mark as resolved so we retry when more levels load
                return;
            }
        }

        hasResolvedLevelSegment.current = true;

        if (initialDraft.segmentName && !selectedSegmentId && resolvedLevelId) {
            const normalizedSegmentName = normalizeTemplateName(initialDraft.segmentName);
            // Match segment to the specific resolved level to avoid cross-level name collisions
            const segment = segments.find(
                (s) => {
                    if (s.levelId !== resolvedLevelId) return false;
                    const normalizedName = normalizeTemplateName(s.name);
                    const normalizedDisplayName = normalizeTemplateName(s.displayName);
                    return normalizedName === normalizedSegmentName || normalizedDisplayName === normalizedSegmentName;
                }
            );
            if (segment) {
                setSelectedSegmentId(segment.id);
            }
        }

        // If no metric names to resolve, mark fully resolved
        if (!initialDraft.metricNames || initialDraft.metricNames.length === 0) {
            hasResolvedMetrics.current = true;
            hasResolvedIds.current = true;
        }
    }, [editWidget, templateConfig, availableLevels, segments, initialDraft, selectedLevelId, selectedSegmentId]);

    // Phase 2: Resolve metric IDs once metrics load (happens after level is set from Phase 1)
    useEffect(() => {
        if (!(editWidget || templateConfig) || !hasResolvedLevelSegment.current || hasResolvedMetrics.current) return;
        if (availableMetrics.length === 0) return;
        if (!initialDraft.metricNames || initialDraft.metricNames.length === 0) return;

        if (selectedMetricIds.length === 0) {
            const resolvedMetricIds = initialDraft.metricNames
                .map((name: string) => {
                    const normalizedName = normalizeTemplateName(name);
                    const metric = availableMetrics.find((m) => {
                        const normalizedMetricName = normalizeTemplateName(m.name);
                        const normalizedShowName = normalizeTemplateName(m.showName);
                        return normalizedMetricName === normalizedName || normalizedShowName === normalizedName;
                    });
                    return metric?.id;
                })
                .filter((id): id is number => id !== undefined);

            // Only mark resolved if we matched at least some metrics — otherwise retry when more load
            if (resolvedMetricIds.length === 0) return;

            setSelectedMetricIds(resolvedMetricIds);
        }

        // Resolve hidden-by-default ("dynamic") metric names → ids against the now-loaded
        // metric pool, mirroring the selected-metric resolution above so edit mode restores
        // the grayed chips. Done here (not at init) because metrics load asynchronously.
        if (initialDraft.dynamicMetricNames && initialDraft.dynamicMetricNames.length > 0) {
            const resolvedDynamicIds = initialDraft.dynamicMetricNames
                .map((name: string) => {
                    const normalizedName = normalizeTemplateName(name);
                    const metric = availableMetrics.find((m) => {
                        const normalizedMetricName = normalizeTemplateName(m.name);
                        const normalizedShowName = normalizeTemplateName(m.showName);
                        return normalizedMetricName === normalizedName || normalizedShowName === normalizedName;
                    });
                    return metric?.id;
                })
                .filter((id): id is number => id !== undefined);
            if (resolvedDynamicIds.length > 0) {
                setDynamicMetricIds(resolvedDynamicIds);
            }
        }

        hasResolvedMetrics.current = true;
        hasResolvedIds.current = true;
    }, [editWidget, templateConfig, availableMetrics, initialDraft, selectedMetricIds]);

    // Keep dynamic (hidden-by-default) metrics a strict subset of the selected metrics:
    // if a metric is removed, it can no longer be marked dynamic. Skip while edit/template
    // ids are still resolving — selectedMetricIds is momentarily empty and would wipe the marks.
    useEffect(() => {
        if ((editWidget || templateConfig) && !hasResolvedIds.current) return;
        setDynamicMetricIds((prev) => {
            const next = prev.filter((id) => selectedMetricIds.includes(id));
            return next.length === prev.length ? prev : next;
        });
    }, [selectedMetricIds, editWidget, templateConfig]);

    const handleToggleDynamicMetric = (id: number) => {
        setDynamicMetricIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const [realWidgetType, setRealWidgetType] = useState<string | undefined>(undefined);

    const isCustomType = widgetType === "custom";

    // Apply template config: set immediate state values and trigger name→ID resolution
    useEffect(() => {
        if (!templateConfig) return;

        hasResolvedLevelSegment.current = false;
        hasResolvedMetrics.current = false;
        hasResolvedIds.current = false;

        // Reset data fields so Phase 1 resolution can re-resolve from the new template
        setSelectedLevelId(null);
        setSelectedSegmentId(null);
        setSelectedMetricIds([]);

        if (templateConfig.title) setTitle(templateConfig.title);
        if (templateConfig.description) setDescription(templateConfig.description);

        const wt = (templateConfig.chartType || templateConfig.widgetType) as WidgetTypeOption;
        if (wt && WIDGET_TYPE_OPTIONS.some((opt) => opt.value === wt)) {
            setWidgetType(wt);
            setRealWidgetType(undefined);
        } else if (wt) {
            // Unmapped type (top-offers, table-map, etc.) — use "custom" and store the real type
            setWidgetType("custom");
            setRealWidgetType(templateConfig.widgetType);
        }

        if (templateConfig.products && templateConfig.products.length > 0) {
            setSelectedProducts(templateConfig.products);
        }
    }, [templateConfig]);

    // Track widget type changes without clearing data selections
    const prevWidgetType = useRef(widgetType);
    useEffect(() => {
        if (prevWidgetType.current === widgetType) return;
        prevWidgetType.current = widgetType;
    }, [widgetType]);

    // True when at least one filter is both enabled and fully specified — those filters
    // would actually take effect on save, so they should block widget types that can't honor them.
    const hasActiveFilters = useMemo(
        () => dataFilters.some((f) => f.enabled !== false && isDraftFilterComplete(f)),
        [dataFilters],
    );

    // Determine which widget types are enabled based on current selections
    const isWidgetTypeEnabled = useMemo(() => {
        const levelName = selectedLevelId ? levels.find((l) => l.id === selectedLevelId)?.name.toLowerCase() : null;
        const isClientLevel = levelName === "client";
        const hasSegmentSelected = selectedSegmentId !== null;
        const metricCount = selectedMetricIds.length;

        // Check if selected segment is time-based (date, month, week)
        const selectedSegmentName = selectedSegmentId ? segments.find((s) => s.id === selectedSegmentId)?.name.toLowerCase() : null;
        const timeBasedSegments = ["date", "month", "week"];
        const isTimeBasedSegment = selectedSegmentName ? timeBasedSegments.includes(selectedSegmentName) : false;

        // LINE: client level + time-based segment + 1+ metrics, OR non-client level + time-based segment + exactly 1 metric
        const lineEnabled = isTimeBasedSegment && ((isClientLevel && metricCount >= 1) || (!isClientLevel && metricCount === 1));

        // FUNNEL: client level only, no segment, 3+ metrics
        const funnelEnabled = isClientLevel && !hasSegmentSelected && metricCount >= 3;

        // BUBBLE:
        // - client level + no segment = 2+ metrics
        // - client level + segment = 1 metric
        // - non-client level + no segment = 1 metric
        // - non-client level + segment = NOT allowed
        const bubbleEnabled =
            (isClientLevel && !hasSegmentSelected && metricCount >= 2) ||
            (isClientLevel && hasSegmentSelected && metricCount === 1) ||
            (!isClientLevel && !hasSegmentSelected && metricCount === 1);

        const enabled = {
            // Ticker / funnel / bubble can't honor data filters (see WIDGET_TYPES_WITHOUT_FILTERS),
            // so disable them once the user has at least one active filter — otherwise they'd pick
            // a type that silently drops the filter work.
            ticker: metricCount === 1 && isClientLevel && !hasSegmentSelected && !hasActiveFilters,
            bar: (!isClientLevel && !hasSegmentSelected) || (isClientLevel && hasSegmentSelected) || (isClientLevel && !hasSegmentSelected),
            line: lineEnabled,
            funnel: funnelEnabled && !hasActiveFilters,
            table: true,
            pie: (isClientLevel && metricCount === 1) || (!isClientLevel && !hasSegmentSelected && metricCount === 1),
            bubble: bubbleEnabled && !hasActiveFilters,
            // Combo chart: exactly 2 metrics (bar + line on dual Y-axes), no segment grouping
            "stacked-bar-line": metricCount === 2 && !hasSegmentSelected,
            custom: isCustomType,
        };

        // Always keep the source widget/template's original type available — these widgets
        // already exist as that type, so the new-widget rules shouldn't retroactively forbid
        // editing or duplicating them (see sourceWidgetType).
        if (sourceWidgetType) {
            enabled[sourceWidgetType] = true;
        }

        return enabled;
    }, [selectedLevelId, selectedSegmentId, selectedMetricIds, groupBySegment, levels, segments, hasActiveFilters, sourceWidgetType, isCustomType]);

    useEffect(() => {
        if ((editWidget || templateConfig) && !hasResolvedIds.current) {
            return;
        }

        if (widgetType && !isWidgetTypeEnabled[widgetType]) {
            setWidgetType(null);
        }
    }, [isWidgetTypeEnabled, widgetType, editWidget, templateConfig]);

    const draft = useMemo<CustomWidgetDraft>(
        () => ({
            title: title.trim(),
            description: description.trim(),
            widgetType,
            productIds: selectedProducts,
            levelId: selectedLevelId,
            segmentId: selectedSegmentId,
            metricIds: selectedMetricIds,
            dynamicMetricIds: widgetType === "table" ? dynamicMetricIds : [],
            groupBySegment,
            drilldownBySegment: false,
            publishToAll,
            widgetColors,
            filterByProduct,
            filterByDimension,
            tableSummaryRow,
            usePageDate,
            datePreset,
            customDateRange,
            productLevel: selectedProducts.length > 1 && productLevel,
            showMetricSelector,
            enableAIInsights,
            publishInsights: enableAIInsights ? publishInsights : false,
            realWidgetType: isCustomType ? realWidgetType : undefined,
            dataFilters,
        }),
        [
            title,
            description,
            widgetType,
            selectedProducts,
            selectedLevelId,
            selectedSegmentId,
            selectedMetricIds,
            dynamicMetricIds,
            groupBySegment,
            false,
            publishToAll,
            widgetColors,
            filterByProduct,
            filterByDimension,
            tableSummaryRow,
            usePageDate,
            datePreset,
            customDateRange,
            productLevel,
            showMetricSelector,
            enableAIInsights,
            publishInsights,
            isCustomType,
            realWidgetType,
            dataFilters,
        ]
    );

    const allFiltersComplete = dataFilters.every(isDraftFilterComplete);
    const isValid =
        draft.title.length > 0 &&
        draft.description.length > 0 &&
        draft.productIds.length > 0 &&
        draft.levelId !== null &&
        (isCustomType || draft.metricIds.length > 0) &&
        draft.widgetType !== null &&
        allFiltersComplete;

    useEffect(() => {
        onDraftChange?.({
            draft,
            isValid,
            isLoading,
            hierarchyData: {
                products: products.map((p) => ({
                    id: p.id,
                    name: p.name,
                    services: services
                        .filter((service) => service.productId === p.id)
                        .map((service) => ({ id: service.id, name: service.name, url: service.url })),
                })),
                levels: levels.map((l) => ({ id: l.id, name: l.name, serviceId: l.serviceId })),
                segments: segments.map((s) => ({ id: s.id, name: s.name, levelId: s.levelId })),
                metrics: metrics.map((m) => ({
                    id: m.id,
                    name: m.name,
                    showName: m.showName,
                    category: m.category,
                    serviceId: m.serviceId,
                })),
            },
        });
    }, [draft, isValid, isLoading, onDraftChange, products, levels, segments, metrics, services]);

    return (
        <form ref={formRef} className="flex h-full flex-col">
            <div className="flex-1 overflow-y-visible">
                <div className="space-y-4">
                    <div className="border p-4 rounded border-muted space-y-3">
                        <Label text="Title" required={true}>
                            <Input type="text" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Enter widget title" required />
                        </Label>

                        <Label text="Tooltip" description="This text will show in the widget info tooltip." required>
                            <Textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Enter description" required />
                        </Label>
                    </div>

                    <div className="p-4 rounded border border-muted">
                        <button type="button" className="flex items-center gap-2 w-full" onClick={() => setIsDataOpen(!isDataOpen)}>
                            <ChartDataIcon className="w-5 h-5" />
                            <span className="font-roboto">Data</span>
                            <ChevronDownIcon className="w-5 h-5 ml-auto" />
                        </button>

                        <div className={isDataOpen ? "max-h-fit overflow-y-visible space-y-3 mt-4 px-1" : "max-h-0 overflow-y-hidden"}>
                            <ProductSelector
                                selectedIds={selectedProducts}
                                products={products}
                                onChange={setSelectedProducts}
                                isLoading={isLoadingProducts}
                                required={true}
                            />

                            {!isCustomType && (
                                <>
                                    <LevelSelector
                                        selectedId={selectedLevelId}
                                        levels={availableLevels}
                                        onChange={setSelectedLevelId}
                                        emptyMessage={!selectedProducts.length ? "Please select at least one product." : "No levels available for selected products."}
                                        isLoading={selectedProducts.length > 0 && isLoadingProductDetails}
                                        required={true}
                                    />

                                    {selectedProducts.length > 1 && (
                                        <Switch
                                            checked={productLevel}
                                            onChange={setProductLevel}
                                            label="Product Level"
                                            description="Show data at product level"
                                        />
                                    )}

                                    <div className="space-y-3">
                                        <SegmentSelector
                                            selectedId={selectedSegmentId}
                                            segments={availableSegments}
                                            onChange={setSelectedSegmentId}
                                            emptyMessage={!selectedLevelId ? "Please select a level first." : "No segments available for selected level."}
                                            isLoading={selectedProducts.length > 0 && isLoadingProductDetails}
                                        />

                                        {selectedSegmentId ? <SegmentOptionsToggle groupBySegment={groupBySegment} onGroupBySegmentChange={setGroupBySegment} /> : null}
                                    </div>

                                    <MetricSelector
                                        selectedIds={selectedMetricIds}
                                        metrics={availableMetrics}
                                        onChange={setSelectedMetricIds}
                                        emptyMessage={
                                            !selectedProducts.length
                                                ? "Select one or more products to see available metrics."
                                                : !selectedLevelId
                                                    ? "Select a level to load the available metrics."
                                                    : "No metrics available for the selected products."
                                        }
                                        isLoading={selectedProducts.length > 0 && !!selectedLevelId && (isLoadingProductDetails || isLoadingMetrics)}
                                        required={true}
                                        dynamicIds={widgetType === "table" || widgetType === null ? dynamicMetricIds : undefined}
                                        onToggleDynamic={widgetType === "table" || widgetType === null ? handleToggleDynamicMetric : undefined}
                                    />
                                </>
                            )}

                            <DateSelector
                                usePageDate={usePageDate}
                                onToggleUsePageDate={setUsePageDate}
                                preset={datePreset}
                                onPresetChange={setDatePreset}
                                customRange={customDateRange}
                                onCustomRangeChange={setCustomDateRange}
                            />
                        </div>
                    </div>

                    {!isCustomType && (
                        <DataFiltersSection
                            filters={dataFilters}
                            fieldOptions={filterFieldOptions}
                            onChange={setDataFilters}
                            isOpen={isFiltersOpen}
                            onToggleOpen={() => setIsFiltersOpen((prev) => !prev)}
                            emptyFieldsMessage="Select a level, segment, or metric above to enable filters."
                            widgetType={widgetType}
                        />
                    )}

                    <div className="border p-4 rounded border-muted">
                        <button type="button" className="flex items-center gap-2 w-full" onClick={() => !isCustomType && setIsWidgetSelectorOpen(!isWidgetSelectorOpen)}>
                            <PaletteIcon className="w-5 h-5" />
                            <span className="font-roboto">Widget Type</span>
                            {isCustomType ? (
                                <span className="ml-auto text-sm text-[#079a95] font-medium font-roboto">Custom</span>
                            ) : (
                                <ChevronDownIcon className="w-5 h-5 ml-auto" />
                            )}
                        </button>

                        {isCustomType && (
                            <div className="mt-2">
                                <button
                                    type="button"
                                    className="text-xs text-[#079a95] hover:text-[#067a76] underline font-roboto"
                                    onClick={() => {
                                        setWidgetType(null);
                                        setRealWidgetType(undefined);
                                    }}
                                >
                                    Switch to manual configuration
                                </button>
                            </div>
                        )}

                        {!isCustomType && (
                            <div className={isWidgetSelectorOpen ? "max-h-96 overflow-y-auto space-y-3 mt-4" : "max-h-0 overflow-y-hidden"}>
                                {hasActiveFilters && (
                                    <div className="flex items-start gap-2 rounded-md border border-[#cfe9e8] bg-[#e8f5f4] px-3 py-2.5 text-[12px] text-[#0a6b67] font-roboto">
                                        <Info className="w-4 h-4 mt-0.5 shrink-0" />
                                        <span>
                                            Ticker, Funnel, and Bubble Chart aren't available while filters are active. Remove the filters to enable these widget types.
                                        </span>
                                    </div>
                                )}
                                <WidgetTypeSelector
                                    selectedType={widgetType}
                                    options={WIDGET_TYPE_OPTIONS}
                                    enabledTypes={isWidgetTypeEnabled}
                                    onChange={setWidgetType}
                                    blockedMessage={
                                        !selectedProducts.length || !selectedMetricIds.length
                                            ? "Provide the data first to view the widget types that best fit this visualization."
                                            : undefined
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <AdvancedSettings
                        isAdvancedOpen={isAdvancedOpen}
                        setIsAdvancedOpen={setIsAdvancedOpen}
                        publishToAll={publishToAll}
                        setPublishToAll={setPublishToAll}
                        showAxisLegends={showAxisLegends}
                        setShowAxisLegends={setShowAxisLegends}
                        widgetColors={widgetColors}
                        setWidgetColors={setWidgetColors}
                        metrics={selectedMetrics}
                        isCampaignSegmentSelected={isCampaignSegmentSelected}
                        campaignSegmentLabel={campaignSegmentLabel}
                        isCampaignLevelSelected={isCampaignLevelSelected}
                        setFilterByProduct={setFilterByProduct}
                        setFilterByDimension={setFilterByDimension}
                        filterByProduct={filterByProduct}
                        filterByDimension={filterByDimension}
                        widgetType={widgetType}
                        tableSummaryRow={tableSummaryRow}
                        setTableSummaryRow={setTableSummaryRow}
                        showMetricSelector={showMetricSelector}
                        setShowMetricSelector={setShowMetricSelector}
                        enableAIInsights={enableAIInsights}
                        setEnableAIInsights={setEnableAIInsights}
                        publishInsights={publishInsights}
                        setPublishInsights={setPublishInsights}
                    />

                    {isError ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 font-roboto">
                            {error || "We couldn't load the product hierarchy. Please try refreshing."}
                        </div>
                    ) : null}
                </div>
            </div>
        </form>
    );
}
